import React, { useState, useEffect } from 'react';
import { useAuthStore } from '../store/useStore';
import { CalendarDays, FileSignature, PackageSearch } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { dashboardAPI } from '../services/dashboardApi';
import { vaiA } from '../utils/destinazione';
import { magazzinoAPI } from '../services/magazzinoApi';
import '../styles/Dashboard.css';

const Dashboard = () => {
    const user = useAuthStore((state) => state.user);
    const logout = useAuthStore((state) => state.logout);
    const navigate = useNavigate();

    const [riepilogo, setRiepilogo] = useState(null);
    const [promemoria, setPromemoria] = useState([]);
    const [consensiMancanti, setConsensiMancanti] = useState([]);
    const [scadenze, setScadenze] = useState([]);
    const [caricamento, setCaricamento] = useState(true);
    const vedeMagazzino = ['admin', 'segreteria'].includes(user?.ruolo);

    useEffect(() => {
        let attivo = true;

        const carica = async () => {
            try {
                const [r, p, c, s] = await Promise.all([
                    dashboardAPI.getRiepilogo(),
                    dashboardAPI.getPromemoria(),
                    dashboardAPI.getConsensiMancanti(),
                    /* Il magazzino e' riservato ad admin e segreteria: per gli
                       altri ruoli la chiamata non si fa nemmeno, e il riquadro
                       non compare. Il .catch resta per il caso in cui il
                       magazzino non risponda: il resto della dashboard non
                       deve cadere con lui. */
                    vedeMagazzino
                        ? magazzinoAPI.scadenze(90).catch(() => ({ data: { data: { lotti: [] } } }))
                        : Promise.resolve({ data: { data: { lotti: [] } } }),
                ]);
                if (!attivo) return;
                setRiepilogo(r.data.data);
                setPromemoria(p.data.data.promemoria || []);
                setConsensiMancanti(c.data.data.visite || []);
                setScadenze(s.data.data.lotti || []);
            } catch (e) {
                console.error('Errore caricamento dashboard:', e);
            } finally {
                if (attivo) setCaricamento(false);
            }
        };

        carica();
        return () => { attivo = false; };
    }, [vedeMagazzino]);

    const handleLogout = () => {
        logout();
        navigate('/login');
    };

    const dataOggi = new Date().toLocaleDateString('it-IT', {
        weekday: 'long', day: 'numeric', month: 'long', year: 'numeric'
    });

    const formatData = (v) => {
        if (!v) return '';
        const d = new Date(v);
        return isNaN(d) ? '' : d.toLocaleDateString('it-IT');
    };

    return (
        <div className="dashboard">

            <div className="dashboard-content">
                <div className="dash-intro">
                    <h1>Ciao {user?.nome}</h1>
                    <span className="dash-data">{dataOggi}</span>
                </div>

                {/* Contatori */}
                <div className="dash-contatori">
                    <div className={`dash-contatore ${(riepilogo?.promemoria_oggi ?? 0) > 0 ? 'attivo' : ''}`}>
                        <span className="dash-num">{caricamento ? '–' : (riepilogo?.promemoria_oggi ?? 0)}</span>
                        <span className="dash-etichetta">Promemoria oggi</span>
                    </div>
                    <div className={`dash-contatore ${(riepilogo?.consensi_mancanti ?? 0) > 0 ? 'allerta' : ''}`}>
                        <span className="dash-num">{caricamento ? '–' : (riepilogo?.consensi_mancanti ?? 0)}</span>
                        <span className="dash-etichetta">Consensi da firmare</span>
                    </div>
                    <button className="dash-contatore" onClick={() => navigate('/pazienti')}>
                        <span className="dash-num">{caricamento ? '–' : (riepilogo?.totale_pazienti ?? 0)}</span>
                        <span className="dash-etichetta">Pazienti in archivio</span>
                    </button>

                    {/* Compare finche' ce n'e' almeno uno: a lavoro finito
                        sparisce invece di restare a zero. */}
                    {(riepilogo?.pazienti_da_verificare ?? 0) > 0 && (
                        <div className="dash-contatore dash-contatore--avviso">
                            <span className="dash-num">
                                {caricamento ? '–' : riepilogo.pazienti_da_verificare.toLocaleString('it-IT')}
                            </span>
                            <span className="dash-etichetta">Anagrafiche da completare</span>
                        </div>
                    )}
                </div>

                <div className="dash-griglia">
                    {/* Promemoria del giorno */}
                    <section className="dash-riquadro">
                        <div className="dash-riquadro-testa">
                            <h2><CalendarDays size={17} strokeWidth={1.7} aria-hidden="true" />Promemoria di oggi</h2>
                        </div>
                        {caricamento ? (
                            <div className="dash-stato">Caricamento…</div>
                        ) : promemoria.length === 0 ? (
                            <div className="dash-vuoto">Nessun promemoria per oggi.</div>
                        ) : (
                            <ul className="dash-lista">
                                {promemoria.map((a) => (
                                    <li key={a.id} className="dash-voce"
                                        onClick={() => vaiA(navigate, a)}>
                                        <span className="dash-ora">{a.ora}</span>
                                        <div className="dash-voce-testo">
                                            <div className="dash-voce-titolo">{a.titolo}</div>
                                            <div className="dash-voce-sub"
                                                 title={`${a.cognome || ''} ${a.nome || ''}${a.descrizione ? ` · ${a.descrizione}` : ''}`.trim()}>
                                                {a.cognome} {a.nome}
                                                {a.descrizione ? ` · ${a.descrizione}` : ''}
                                            </div>
                                        </div>
                                    </li>
                                ))}
                            </ul>
                        )}
                    </section>

                    {/* Consensi mancanti */}
                    <section className="dash-riquadro">
                        <div className="dash-riquadro-testa">
                            <h2><FileSignature size={17} strokeWidth={1.7} aria-hidden="true" />Consensi da firmare</h2>
                        </div>
                        {caricamento ? (
                            <div className="dash-stato">Caricamento…</div>
                        ) : consensiMancanti.length === 0 ? (
                            <div className="dash-vuoto">Tutte le procedure hanno il consenso firmato.</div>
                        ) : (
                            <ul className="dash-lista">
                                {/* La destinazione arriva col dato, come per gli
                                    avvisi: qui non si sa piu' dove porta un
                                    consenso mancante, e non e' un'informazione che
                                    una schermata debba avere. Il perche' sta nella
                                    query, accanto alle colonne. */}
                                {consensiMancanti.map((v) => (
                                    <li key={v.visita_id} className="dash-voce allerta"
                                        onClick={() => vaiA(navigate, v)}>
                                        <span className="dash-badge-data">{formatData(v.data_visita)}</span>
                                        <div className="dash-voce-testo">
                                            <div className="dash-voce-titolo">{v.cognome} {v.nome}</div>
                                            <div className="dash-voce-sub"
                                                 title={`${v.prestazioni || ''}${v.medico_cognome ? ` · Dr. ${v.medico_cognome}` : ''}`}>
                                                {v.prestazioni}
                                                {v.medico_cognome ? ` · Dr. ${v.medico_cognome}` : ''}
                                            </div>
                                            {/* Il testo del segno viene dal server
                                                (segno_del_consenso, migration 292). */}
                                            {v.segno_consenso && (
                                                <div className="dash-voce-segno">Consenso firmato: {v.segno_consenso}</div>
                                            )}
                                        </div>
                                    </li>
                                ))}
                            </ul>
                        )}
                    </section>

                    {/* Scadenze prodotti — dai lotti del magazzino.
                        Solo per chi il magazzino puo' vederlo: agli altri il
                        riquadro direbbe sempre "nessun lotto in scadenza", che
                        e' una risposta falsa, non una risposta vuota. */}
                    {vedeMagazzino && (
                    <section className="dash-riquadro">
                        <div className="dash-riquadro-testa">
                            <h2><PackageSearch size={17} strokeWidth={1.7} aria-hidden="true" />Scadenze nei prossimi 90 giorni</h2>
                        </div>
                        {scadenze.length === 0 ? (
                            <div className="dash-vuoto">
                                <p>Nessun lotto in scadenza nei prossimi 90 giorni.</p>
                            </div>
                        ) : (
                            <ul className="dash-elenco">
                                {scadenze.slice(0, 8).map((l) => {
                                    const giorni = l.giorni_alla_scadenza;
                                    const scaduto = giorni < 0;
                                    return (
                                        /* Fino alla migration 115 questa voce non
                                           poteva essere cliccabile: il riferimento
                                           pretendeva un paziente, e un lotto non ce
                                           l'ha. Ora porta al magazzino, e dove porta
                                           lo dice il dato, non questa riga. */
                                        <li key={l.id}
                                            className={scaduto ? 'dash-voce dash-voce--allarme' : 'dash-voce'}
                                            onClick={() => vaiA(navigate, l)}>
                                            <span className="dash-voce-titolo">{l.nome}</span>
                                            <span className="dash-voce-nota">
                                                lotto {l.codice_lotto || '—'}
                                                {' · '}
                                                {parseFloat(l.quantita_residua).toLocaleString('it-IT')} rimasti
                                                {' · '}
                                                {scaduto
                                                    ? `SCADUTO da ${Math.abs(giorni)} giorni`
                                                    : `scade fra ${giorni} giorni`}
                                            </span>
                                        </li>
                                    );
                                })}
                                {scadenze.length > 8 && (
                                    <li className="dash-voce dash-voce--altro">
                                        e altri {scadenze.length - 8} lotti
                                    </li>
                                )}
                            </ul>
                        )}
                    </section>
                    )}
                </div>
            </div>
        </div>
    );
};

export default Dashboard;
